import React, { useMemo, useState } from 'react';
import Pagination from '../../Pagination/Paginations';
import EntriesSelector from '../../Pagination/EntriesSelector';
import DownloadButtons from '../../DownloadButtons/DownloadButtons';
import useTableSort, { sortableHeaderProps } from './useTableSort';
import Photo from './Photo';
import { photoUrl, resumeUrl } from '../api';

/** Resume view / download buttons plus whatever row actions the tab adds. */
export const RowActions = ({ row, onEdit, onDelete }) => {
  const view = resumeUrl(row.resume_doc);
  const download = resumeUrl(row.resume_doc, { download: true });

  return (
    <div className="d-flex" style={{ gap: 4 }}>
      {view && (
        <a href={view} target="_blank" rel="noopener noreferrer" title="View resume">
          <button type="button" className="btn btn-outline-secondary btn-sm jm-action-pdf">
            <i className="fa fa-file-pdf" aria-hidden="true" />
          </button>
        </a>
      )}
      {download && (
        // Opened in a new tab so a missing file reports itself there, not by
        // navigating this page away from the candidate list.
        <a href={download} target="_blank" rel="noopener noreferrer" title="Download resume">
          <button type="button" className="btn btn-outline-secondary btn-sm">
            <i className="fa fa-download" aria-hidden="true" />
          </button>
        </a>
      )}
      {onDelete && (
        <button type="button" className="btn btn-outline-secondary btn-sm" title="Delete" onClick={() => onDelete(row)}>
          <i className="fa fa-trash" aria-hidden="true" />
        </button>
      )}
      {onEdit && (
        <button type="button" className="btn btn-outline-secondary btn-sm" title="Edit" onClick={() => onEdit(row)}>
          <i className="fa fa-pencil" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

/** The candidate's photo next to their name, as the old app's lists showed it. */
export const NameCell = ({ row }) => {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ');

  return (
    <div className="d-flex align-items-center" style={{ gap: 8 }}>
      <Photo src={photoUrl(row.emp_img)} size={32} name={name} />
      <span>{name}</span>
    </div>
  );
};

/** Tones map to the theme palette in App.css, not to Bootstrap's badge colours. */
export const StatusBadge = ({ label, tone = 'primary' }) => (
  <span className={`badge jm-badge jm-badge-${tone}`}>{label}</span>
);

/**
 * The list every tab renders: search box, page size, export buttons, table,
 * pagination. `columns` is [{ key, label, render?, search? }].
 */
const CandidateTable = ({
  title,
  rows,
  columns,
  loading,
  emptyText = 'No candidates in this stage.',
  exportName = 'job_candidates',
  toolbar,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const searchable = useMemo(
    () => columns.filter((c) => c.search !== false).map((c) => c.key),
    [columns]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      searchable.some((key) => String(row[key] ?? '').toLowerCase().includes(term))
    );
  }, [rows, search, searchable]);

  const { sorted, sort, toggle, indicator } = useTableSort(filtered, columns);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  // Exports carry the plain values in the order shown, not the rendered cells.
  const exportRows = useMemo(
    () =>
      sorted.map((row) =>
        columns.reduce((acc, column) => {
          if (column.key === 'action') return acc;
          const value = column.sortValue ? column.sortValue(row) : row[column.key];
          acc[column.label] = value ?? '';
          return acc;
        }, {})
      ),
    [sorted, columns]
  );

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
        <h6 className="mb-0">{title} <span className="text-muted">({filtered.length})</span></h6>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
          {toolbar}
          <DownloadButtons data={exportRows} filename={exportName} />
          <EntriesSelector entriesPerPage={perPage} setEntriesPerPage={setPerPage} setCurrentPage={setPage} />
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: 220 }}
            placeholder="Search…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-striped table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} {...sortableHeaderProps(column, { toggle })}>
                    {column.label}
                    {column.sortable !== false && (
                      <span
                        className="ml-1"
                        style={{
                          fontSize: '0.7rem',
                          opacity: sort.key === column.key ? 1 : 0.35,
                        }}
                      >
                        {indicator(column.key)}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length} className="text-center py-4">Loading…</td></tr>
              )}
              {!loading && visible.length === 0 && (
                <tr><td colSpan={columns.length} className="text-center py-4 text-muted">{emptyText}</td></tr>
              )}
              {!loading && visible.map((row) => (
                <tr key={row.application_id}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      {column.render ? column.render(row) : (row[column.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

export default CandidateTable;
